import {
  AlertTriangle,
  Button,
  CheckCircle,
  XCircle,
} from "@ews-admin/global-design-system";
import React from "react";
import { useTranslation } from "react-i18next";
import { Modal } from "../Modal/Modal";

interface ConfirmationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  message: string;
  confirmLabel?: string;
  cancelLabel?: string;
  variant?: "danger" | "warning" | "success" | "info";
  isLoading?: boolean;
  icon?: React.ReactNode;
}

export const ConfirmationModal: React.FC<ConfirmationModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
  confirmLabel,
  cancelLabel,
  variant = "danger",
  isLoading = false,
  icon,
}) => {
  const { t } = useTranslation();

  const getVariantStyles = () => {
    switch (variant) {
      case "danger":
        return {
          icon: <AlertTriangle className="w-6 h-6 text-danger-500" />,
          confirmButton: "bg-danger-500 hover:bg-danger-600 text-white",
          iconBg: "bg-danger-50",
        };
      case "warning":
        return {
          icon: <AlertTriangle className="w-6 h-6 text-warning-500" />,
          confirmButton: "bg-warning-500 hover:bg-warning-600 text-white",
          iconBg: "bg-warning-50",
        };
      case "success":
        return {
          icon: <CheckCircle className="w-6 h-6 text-success-500" />,
          confirmButton: "bg-success-500 hover:bg-success-600 text-white",
          iconBg: "bg-success-50",
        };
      case "info":
        return {
          icon: <XCircle className="w-6 h-6 text-ews-primary" />,
          confirmButton: "bg-ews-primary hover:bg-primary-600 text-white",
          iconBg: "bg-primary-50",
        };
      default:
        return {
          icon: <AlertTriangle className="w-6 h-6 text-danger-500" />,
          confirmButton: "bg-danger-500 hover:bg-danger-600 text-white",
          iconBg: "bg-danger-50",
        };
    }
  };

  const styles = getVariantStyles();

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="">
      <div className="p-6">
        <div className="flex items-start space-x-4">
          <div
            className={`flex-shrink-0 w-12 h-12 rounded-full flex items-center justify-center ${styles.iconBg}`}
          >
            {icon || styles.icon}
          </div>
          <div className="flex-1">
            <h3 className="text-lg font-medium text-neutral-900 mb-2">
              {title}
            </h3>
            <p className="text-sm text-neutral-600 mb-6">{message}</p>
            <div className="flex justify-end space-x-3">
              <Button
                variant="outline"
                onClick={onClose}
                disabled={isLoading}
                className="px-4 py-2"
              >
                {cancelLabel || t("common.cancel")}
              </Button>
              <Button
                onClick={onConfirm}
                disabled={isLoading}
                className={`px-4 py-2 ${styles.confirmButton}`}
              >
                {isLoading ? (
                  <div className="flex items-center space-x-2">
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    <span>{t("common.processing")}</span>
                  </div>
                ) : (
                  confirmLabel || t("common.confirm")
                )}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </Modal>
  );
};
